/**
 * cashDrawerSecurityConfig.ts — Reglas de tope de efectivo (config por negocio) + ventanas de bypass.
 *
 * QUÉ HACE: La CONFIGURACIÓN del negocio se lee/escribe vía transactionalStore
 * (`txReadRaw`/`txWriteRaw`, regla ACID). Las ventanas de bypass usan
 * sessionStorage porque son estado de sesión efímero con TTL (mueren al cerrar
 * la pestaña) — excepción documentada en .eslintrc.cjs.
 * Emite `altenot:cash-drawer-security` / `altenot:cash-drawer-bypass` para que
 * la vista de cobro recargue sin recargar la página.
 * QUÉ NO DEBE HACER: No calcula montos en caja ni valida contraseñas.
 */
import type { CashDrawerSecurityConfig } from "../types/cashDrawerSecurity.types";
import { txReadRaw, txWriteRaw } from "@/lib/persistence/transactionalStore";

const LEGACY_KEY = "altenot_cash_drawer_security_global";

export const DEFAULT_CASH_DRAWER_SECURITY: CashDrawerSecurityConfig = {
  enabled: false,
  warnThresholdCop: 400_000,
  blockThresholdCop: 600_000,
  bypassTtlMinutes: 30,
  adminSalesGateEnabled: false,
  adminSalesGateEvery: 10,
  requireIdentificationOnCharge: true,
  requireAuthorizationToRemoveCartItem: false,
};

/** Aviso unificado (barra de caja + panel de cobro): tope duro + ventana extra ya usada o vencida. */
export const CASH_DRAWER_SHELF_CRITICAL_COPY =
  "Tope de efectivo en gaveta y tiempo extra de venta ya agotado: no podrás cobrar en efectivo en el siguiente cobro hasta retirar en Movimientos o usar otro medio (tarjeta, Nequi, etc.).";

function storageKeyForBusiness(businessId: string): string {
  const id = (businessId || "default").trim() || "default";
  return `altenot_cash_drawer_security_${id}`;
}

function clampInt(
  n: unknown,
  min: number,
  max: number,
  fallback: number,
): number {
  const v = typeof n === "number" ? n : parseInt(String(n), 10);
  if (!Number.isFinite(v)) return fallback;
  return Math.min(max, Math.max(min, Math.floor(v)));
}

export function normalizeCashDrawerSecurity(
  raw: Partial<CashDrawerSecurityConfig> | null | undefined,
): CashDrawerSecurityConfig {
  const base = { ...DEFAULT_CASH_DRAWER_SECURITY, ...raw };
  const warn = clampInt(base.warnThresholdCop, 0, 999_999_999, 0);
  let block = clampInt(base.blockThresholdCop, 0, 999_999_999, 0);
  if (warn > 0 && block > 0 && block <= warn) {
    block = warn + 50_000;
  }
  return {
    enabled: !!base.enabled,
    warnThresholdCop: warn,
    blockThresholdCop: block,
    bypassTtlMinutes: clampInt(base.bypassTtlMinutes, 5, 240, 30),
    adminSalesGateEnabled: !!base.adminSalesGateEnabled,
    adminSalesGateEvery: clampInt(base.adminSalesGateEvery, 2, 99, 10),
    requireIdentificationOnCharge: resolveRequireIdentificationOnCharge(base),
    requireAuthorizationToRemoveCartItem:
      base.requireAuthorizationToRemoveCartItem === true,
  };
}

/** Migra claves legacy (requirePasswordOnCharge, etc.) al toggle único. */
function resolveRequireIdentificationOnCharge(
  raw: Partial<CashDrawerSecurityConfig> & {
    requirePasswordOnCharge?: boolean;
    allowCredentialOnCharge?: boolean;
  },
): boolean {
  if (typeof raw.requireIdentificationOnCharge === "boolean") {
    return raw.requireIdentificationOnCharge;
  }
  const legacyPass = raw.requirePasswordOnCharge;
  const legacyCred = raw.allowCredentialOnCharge;
  if (legacyPass === false && legacyCred === false) return false;
  if (legacyPass === false && legacyCred === true) return true;
  if (legacyPass === true && legacyCred === false) return true;
  return legacyPass !== false;
}

export function loadCashDrawerSecurityConfig(
  businessId: string | undefined | null,
): CashDrawerSecurityConfig {
  const bid = businessId || "default";
  try {
    const key = storageKeyForBusiness(bid);
    let raw = txReadRaw(key);
    if (!raw && bid !== "default") {
      raw = txReadRaw(LEGACY_KEY);
    }
    if (!raw) return { ...DEFAULT_CASH_DRAWER_SECURITY };
    return normalizeCashDrawerSecurity(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_CASH_DRAWER_SECURITY };
  }
}

export function persistCashDrawerSecurityConfig(
  businessId: string | undefined | null,
  cfg: CashDrawerSecurityConfig,
): CashDrawerSecurityConfig {
  const clean = normalizeCashDrawerSecurity(cfg);
  const key = storageKeyForBusiness(businessId || "default");
  txWriteRaw(key, JSON.stringify(clean));
  try {
    window.dispatchEvent(new CustomEvent("altenot:cash-drawer-security"));
  } catch {
    /* SSR / entornos sin window */
  }
  return clean;
}

/** Punto de venta usado en sessionStorage del bypass; si no hay id aún, una sola caja comparte `default`. */
export function effectiveBypassPuntoId(
  puntoCajaId: string | undefined | null,
): string {
  const p = (puntoCajaId || "").trim();
  return p || "default";
}

/** sessionStorage: ventana temporal por punto de venta (multicaja). */
export function bypassStorageKey(
  businessId: string | undefined | null,
  puntoCajaId: string,
): string {
  const b = (businessId || "default").trim() || "default";
  const p = effectiveBypassPuntoId(puntoCajaId);
  return `altenot_caja_efectivo_bypass_${b}_${p}`;
}

export function readBypassExpiresAt(
  businessId: string | undefined | null,
  puntoCajaId: string,
): number | null {
  try {
    const raw = sessionStorage.getItem(
      bypassStorageKey(businessId, puntoCajaId),
    );
    if (!raw) return null;
    const o = JSON.parse(raw) as { until?: number };
    return typeof o.until === "number" && Number.isFinite(o.until)
      ? o.until
      : null;
  } catch {
    return null;
  }
}

export function isCashDrawerBypassActive(
  businessId: string | undefined | null,
  puntoCajaId: string,
): boolean {
  const until = readBypassExpiresAt(businessId, puntoCajaId);
  return !!until && until > Date.now();
}

function uniqEffectiveBypassIds(
  candidates: (string | undefined | null)[],
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const c of candidates) {
    const id = effectiveBypassPuntoId(c);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/** Primera clave (punto) donde haya ventana de bypass vigente. */
export function getFirstActiveBypassPuntoId(
  businessId: string | undefined | null,
  candidates: (string | undefined | null)[],
): string | null {
  for (const id of uniqEffectiveBypassIds(candidates)) {
    if (isCashDrawerBypassActive(businessId, id)) return id;
  }
  return null;
}

/** True si el bypass está activo en cualquiera de las claves dadas (multicaja / sesión vs selección). */
export function isCashDrawerBypassActiveAny(
  businessId: string | undefined | null,
  candidates: (string | undefined | null)[],
): boolean {
  return getFirstActiveBypassPuntoId(businessId, candidates) != null;
}

/** Minutos enteros hasta que caduca la autorización de cola; `null` si no hay ventana activa. */
export function getBypassMinutesRemaining(
  businessId: string | undefined | null,
  puntoCajaId: string | undefined | null,
): number | null {
  const pid = effectiveBypassPuntoId(puntoCajaId);
  const until = readBypassExpiresAt(businessId, pid);
  if (until == null || until <= Date.now()) return null;
  return Math.max(0, Math.ceil((until - Date.now()) / 60_000));
}

/** Texto vivo del tiempo restante (min/s) hasta caducar la ventana de cola. */
export function getBypassCountdownLabel(
  businessId: string | undefined | null,
  puntoCajaId: string | undefined | null,
): string | null {
  const pid = effectiveBypassPuntoId(puntoCajaId);
  const until = readBypassExpiresAt(businessId, pid);
  if (until == null || until <= Date.now()) return null;
  const totalSec = Math.max(0, Math.ceil((until - Date.now()) / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) {
    return `${h} h ${m} min restantes del tiempo extra para vender en efectivo`;
  }
  if (m > 0) {
    return `${m} min ${String(s).padStart(2, "0")} s restantes del tiempo extra para vender en efectivo`;
  }
  return `${s} s restantes del tiempo extra para vender en efectivo`;
}

/** Cuenta regresiva usando la primera ventana activa encontrada entre candidatos. */
export function getBypassCountdownFromCandidates(
  businessId: string | undefined | null,
  candidates: (string | undefined | null)[],
): string | null {
  const pid = getFirstActiveBypassPuntoId(businessId, candidates);
  if (!pid) return null;
  return getBypassCountdownLabel(businessId, pid);
}

export function grantCashDrawerBypass(
  businessId: string | undefined | null,
  puntoCajaId: string,
  ttlMinutes: number,
): void {
  const pid = effectiveBypassPuntoId(puntoCajaId);
  const mins = clampInt(ttlMinutes, 5, 240, 30);
  const until = Date.now() + mins * 60_000;
  sessionStorage.setItem(
    bypassStorageKey(businessId, pid),
    JSON.stringify({ until }),
  );
  try {
    window.dispatchEvent(new CustomEvent("altenot:cash-drawer-bypass"));
  } catch {
    /* noop */
  }
}

export function clearCashDrawerBypass(
  businessId: string | undefined | null,
  puntoCajaId: string,
): void {
  const pid = effectiveBypassPuntoId(puntoCajaId);
  sessionStorage.removeItem(bypassStorageKey(businessId, pid));
  try {
    window.dispatchEvent(new CustomEvent("altenot:cash-drawer-bypass"));
  } catch {
    /* noop */
  }
}

/** Limpia la ventana extra en todas las claves candidatas (misma sesión, distintos ids). */
export function clearCashDrawerBypassOnAllCandidates(
  businessId: string | undefined | null,
  candidates: (string | undefined | null)[],
): void {
  for (const id of uniqEffectiveBypassIds(candidates)) {
    sessionStorage.removeItem(bypassStorageKey(businessId, id));
  }
  try {
    window.dispatchEvent(new CustomEvent("altenot:cash-drawer-bypass"));
  } catch {
    /* noop */
  }
}

/** Quita entradas de bypass ya vencidas (evita estado UI zombie). No dispara eventos. */
export function clearExpiredBypassForCandidates(
  businessId: string | undefined | null,
  candidates: (string | undefined | null)[],
): void {
  const sid = (businessId || "default").trim() || "default";
  for (const id of uniqEffectiveBypassIds(candidates)) {
    const until = readBypassExpiresAt(sid, id);
    if (until != null && until <= Date.now()) {
      sessionStorage.removeItem(bypassStorageKey(sid, id));
    }
  }
}
